import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { FiUser, FiMail, FiBriefcase, FiEdit2, FiSave, FiX, FiLock, FiAlertCircle, FiCheckCircle, FiShield, FiSmartphone, FiCopy } from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';

const ProfilePage: React.FC = () => {
  const { activePersona } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: activePersona.name,
    email: activePersona.email,
    title: activePersona.title,
    role: activePersona.role,
  });

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 2FA state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [enrollmentData, setEnrollmentData] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [enrollmentStep, setEnrollmentStep] = useState<'list' | 'setup' | 'verify'>('list');

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: activePersona.name,
      email: activePersona.email,
      title: activePersona.title,
      role: activePersona.role,
    });
    setIsEditing(false);
  };

  // Password validation function
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};\':"|<>?,./~]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate inputs
    if (!passwordData.currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword === passwordData.currentPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(passwordData.newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setIsChangingPassword(true);

    try {
      // First, verify current password by attempting to sign in
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user?.email) {
        setPasswordError('Unable to verify user session. Please sign in again.');
        setIsChangingPassword(false);
        return;
      }

      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        setPasswordError('Current password is incorrect');
        setIsChangingPassword(false);
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) {
        setPasswordError(updateError.message || 'Failed to update password');
        setIsChangingPassword(false);
        return;
      }

      // Success
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);

    } catch (error) {
      setPasswordError('An unexpected error occurred. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  // 2FA Functions
  const loadMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      setMfaFactors(data.all || []);
    } catch (error) {
      console.error('Error loading MFA factors:', error);
      setMfaError('Failed to load two-factor authentication settings');
    }
  };

  const handle2FAModalOpen = async () => {
    setShow2FAModal(true);
    setEnrollmentStep('list');
    setMfaError('');
    setMfaSuccess('');
    await loadMFAFactors();
  };

  const handle2FAModalClose = () => {
    setShow2FAModal(false);
    setEnrollmentStep('list');
    setEnrollmentData(null);
    setVerificationCode('');
    setMfaError('');
    setMfaSuccess('');
  };

  const startEnrollment = async () => {
    setIsProcessing(true);
    setMfaError('');
    
    try {
      // Clean up unverified factors first (best practice)
      const unverifiedFactors = mfaFactors.filter(f => f.status === 'unverified');
      for (const factor of unverifiedFactors) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }

      // Start enrollment
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error) throw error;

      setEnrollmentData(data);
      setEnrollmentStep('setup');
    } catch (error: any) {
      setMfaError(error.message || 'Failed to start enrollment');
    } finally {
      setIsProcessing(false);
    }
  };

  const verifyEnrollment = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setMfaError('Please enter a valid 6-digit code');
      return;
    }

    setIsProcessing(true);
    setMfaError('');

    try {
      // Create challenge
      const challengeResponse = await supabase.auth.mfa.challenge({
        factorId: enrollmentData.id,
      });

      if (challengeResponse.error) throw challengeResponse.error;

      // Verify the code
      const verifyResponse = await supabase.auth.mfa.verify({
        factorId: enrollmentData.id,
        challengeId: challengeResponse.data.id,
        code: verificationCode,
      });

      if (verifyResponse.error) throw verifyResponse.error;

      // Success!
      setMfaSuccess('Two-factor authentication enabled successfully!');
      setVerificationCode('');
      setEnrollmentData(null);
      
      // Reload factors and go back to list
      await loadMFAFactors();
      setTimeout(() => {
        setEnrollmentStep('list');
        setMfaSuccess('');
      }, 2000);
    } catch (error: any) {
      setMfaError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const removeFactor = async (factorId: string) => {
    if (!confirm('Are you sure you want to remove this two-factor authentication method?')) {
      return;
    }

    setIsProcessing(true);
    setMfaError('');

    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;

      setMfaSuccess('Two-factor authentication removed successfully');
      await loadMFAFactors();
      
      setTimeout(() => setMfaSuccess(''), 3000);
    } catch (error: any) {
      setMfaError(error.message || 'Failed to remove two-factor authentication');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMfaSuccess('Secret key copied to clipboard!');
      setTimeout(() => setMfaSuccess(''), 2000);
    } catch (error) {
      setMfaError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Profile</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your personal information and preferences</p>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors"
            >
              <FiEdit2 size={16} />
              <span className="hidden sm:inline">Edit Profile</span>
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors"
              >
                <FiX size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors"
              >
                <FiSave size={16} />
                Save Changes
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Profile Information Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200">
              <img
                src={activePersona.avatar}
                alt={activePersona.name}
                className="w-20 h-20 rounded-full bg-slate-200"
              />
              <div>
                <h2 className="text-xl font-semibold text-slate-800">{activePersona.name}</h2>
                <p className="text-sm text-slate-500">{activePersona.title}</p>
                <p className="text-xs text-slate-400 mt-1">{activePersona.role}</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-700 mb-4">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <FiUser className="inline mr-2" size={14} />
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  ) : (
                    <p className="text-slate-600 bg-slate-50 px-3 py-2 rounded-md">{formData.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <FiMail className="inline mr-2" size={14} />
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  ) : (
                    <p className="text-slate-600 bg-slate-50 px-3 py-2 rounded-md">{formData.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <FiBriefcase className="inline mr-2" size={14} />
                    Job Title
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  ) : (
                    <p className="text-slate-600 bg-slate-50 px-3 py-2 rounded-md">{formData.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <FiBriefcase className="inline mr-2" size={14} />
                    Role
                  </label>
                  <p className="text-slate-600 bg-slate-50 px-3 py-2 rounded-md">{formData.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-slate-700 mb-4">Account Settings</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <p className="font-medium text-slate-700">Email Notifications</p>
                  <p className="text-sm text-slate-500">Receive email updates about member activities</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <p className="font-medium text-slate-700">Dashboard Alerts</p>
                  <p className="text-sm text-slate-500">Show alerts for critical member status changes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-slate-700">Weekly Reports</p>
                  <p className="text-sm text-slate-500">Receive weekly summary reports via email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Security Settings Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-slate-700 mb-4">Security</h3>
            
            <div className="space-y-4">
              <div>
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="text-sm text-slate-700 hover:text-slate-900 font-medium underline"
                >
                  Change Password
                </button>
              </div>
              <div>
                <button 
                  onClick={handle2FAModalOpen}
                  className="text-sm text-slate-700 hover:text-slate-900 font-medium underline"
                >
                  Two-Factor Authentication
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <FiLock className="text-slate-600" />
                Change Password
              </h2>
              <button
                onClick={handleClosePasswordModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Enter current password"
                  disabled={isChangingPassword}
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Enter new password"
                  disabled={isChangingPassword}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Confirm new password"
                  disabled={isChangingPassword}
                />
              </div>

              {/* Password Requirements */}
              <div className="bg-slate-50 rounded-md p-3 text-xs text-slate-600">
                <p className="font-medium mb-1">Password must contain:</p>
                <ul className="space-y-1">
                  <li>• At least 8 characters</li>
                  <li>• One uppercase letter (A-Z)</li>
                  <li>• One lowercase letter (a-z)</li>
                  <li>• One number (0-9)</li>
                  <li>• One special character (!@#$%^&*...)</li>
                </ul>
              </div>

              {/* Error Message */}
              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                  <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">{passwordError}</p>
                </div>
              )}

              {/* Success Message */}
              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-start gap-2">
                  <FiCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-green-700">{passwordSuccess}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClosePasswordModal}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium"
                  disabled={isChangingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors font-medium disabled:bg-slate-400 disabled:cursor-not-allowed"
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Two-Factor Authentication Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <FiShield className="text-slate-600" />
                Two-Factor Authentication
              </h2>
              <button
                onClick={handle2FAModalClose}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Error Message */}
            {mfaError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2 mb-4">
                <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-red-700">{mfaError}</p>
              </div>
            )}

            {/* Success Message */}
            {mfaSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-start gap-2 mb-4">
                <FiCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-green-700">{mfaSuccess}</p>
              </div>
            )}

            {/* Step 1: List Factors */}
            {enrollmentStep === 'list' && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-md p-4">
                  <p className="text-sm text-slate-700 mb-2">
                    Two-factor authentication adds an extra layer of security to your account by requiring a verification code from your phone in addition to your password.
                  </p>
                  <p className="text-sm text-slate-600">
                    You'll need an authenticator app like Google Authenticator, Authy, or 1Password.
                  </p>
                </div>

                {/* Existing Factors */}
                {mfaFactors.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-medium text-slate-700">Active Methods:</h3>
                    {mfaFactors.map((factor) => (
                      <div key={factor.id} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FiSmartphone className="text-slate-600" size={20} />
                          <div>
                            <p className="font-medium text-slate-800">
                              {factor.friendly_name || 'Authenticator App'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {factor.status === 'verified' ? 'Active' : 'Pending verification'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFactor(factor.id)}
                          disabled={isProcessing}
                          className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Factor Button */}
                {mfaFactors.filter(f => f.status === 'verified').length === 0 && (
                  <button
                    onClick={startEnrollment}
                    disabled={isProcessing}
                    className="w-full px-4 py-3 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors font-medium disabled:bg-slate-400 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                  </button>
                )}

                {mfaFactors.filter(f => f.status === 'verified').length > 0 && (
                  <button
                    onClick={startEnrollment}
                    disabled={isProcessing}
                    className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium disabled:opacity-50"
                  >
                    Add Backup Method
                  </button>
                )}
              </div>
            )}

            {/* Step 2: Setup - Show QR Code */}
            {enrollmentStep === 'setup' && enrollmentData && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="font-medium text-slate-800 mb-2">Scan QR Code</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Scan this QR code with your authenticator app
                  </p>
                  
                  {/* QR Code */}
                  <div className="bg-white p-4 rounded-lg border-2 border-slate-200 inline-block">
                    <img
                      src={`data:image/svg+xml;utf8,${encodeURIComponent(enrollmentData.totp.qr_code)}`}
                      alt="QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-700 font-medium mb-2">Can't scan the code?</p>
                  <p className="text-xs text-slate-600 mb-2">Enter this secret key manually:</p>
                  
                  <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-md">
                    <code className="flex-1 text-sm font-mono text-slate-800 break-all">
                      {enrollmentData.totp.secret}
                    </code>
                    <button
                      onClick={() => copyToClipboard(enrollmentData.totp.secret)}
                      className="flex-shrink-0 p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      <FiCopy size={16} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => setEnrollmentStep('verify')}
                  className="w-full px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors font-medium"
                >
                  Continue to Verification
                </button>

                <button
                  onClick={() => {
                    setEnrollmentStep('list');
                    setEnrollmentData(null);
                  }}
                  className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Step 3: Verify Code */}
            {enrollmentStep === 'verify' && enrollmentData && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="font-medium text-slate-800 mb-2">Enter Verification Code</h3>
                  <p className="text-sm text-slate-600">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                <div>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 font-mono"
                    placeholder="000000"
                    maxLength={6}
                    disabled={isProcessing}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setEnrollmentStep('setup')}
                    className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium"
                    disabled={isProcessing}
                  >
                    Back
                  </button>
                  <button
                    onClick={verifyEnrollment}
                    disabled={isProcessing || verificationCode.length !== 6}
                    className="flex-1 px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors font-medium disabled:bg-slate-400 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
